import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { isAfter, isBefore, isEqual } from 'date-fns';
import { IOperatorsDTO, IUserOperatorDTO, OperatorRole } from '../../../../../api/operators.dao';
import { IRoomDetailsDTO } from '../../../../../api/rooms.dao';
import { SHORT_PLAIN_TEXT_LENGTH, ConnectionVersion } from '../../../../../constants/common.constants';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { utcToZonedTime, zonedTimeToUtc } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { OperatorItemsMapper } from '../../../../../modules/operators/operator-items.mapper';
import { CANNOT_BE_EQUAL_OR_GREATER_TEXT, FIELD_IS_REQUIRED_TEXT, INVALID_END_TIME_TEXT, INVALID_START_TIME_OR_END_TIME_TEXT, INVALID_START_TIME_TEXT, ROOM_LABEL_MAX_LENGTH_TEXT, SHOULD_NOT_BEFORE_START_TEXT } from './room-form.texts';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl/lib';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';

const defaultValues: IRoomDetailsDTO = {
    roomId: null,
    name: null,
    label: '',
    endDateTime: null,
    presenters: [],
    startDateTime: null,
    operators: [],
    hostRoleConnectionId: null
};
type InstanceRefType = ThisType<RoomFormDataVM>;
class RoomFormDataVM {
    public static mapItemsByKey<T>(items: T[], key: string, ref: InstanceRefType): void {
        items.forEach(function(item) {
            this[item[key]] = item;
        }, ref);
    }
    @mobx.observable
    public roomId: number;
    @mobx.observable
    public name: string;
    @mobx.observable
    public label: string;
    @mobx.observable
    public endDateTime: Date;
    @mobx.observable
    public startDateTime: Date;
    @mobx.observable
    public startDate: Date;
    @mobx.observable
    public endDate: Date;
    @mobx.observable
    public startTimeValidationError: boolean = false;
    @mobx.observable
    public endTimeValidationError: boolean = false;
    @mobx.observable
    public hostRoleConnectionId: number | null;

    @mobx.observable
    private formChecked: boolean;
    @mobx.observable
    private operators: { [email: string]: IUserOperatorDTO } = {};
    @mobx.observable
    private validationErrors: Record<string, JSX.Element> = {};
    @mobx.observable
    private connectionVersion: boolean;

    private readonly operatorsMapper: OperatorItemsMapper;

    constructor(
        roomDetailsDTO: IRoomDetailsDTO = defaultValues,
        private readonly eventDTO: IEventDetailsDTO,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        const { mapItemsByKey } = RoomFormDataVM;
        this.roomId = roomDetailsDTO.roomId || defaultValues.roomId;
        this.name = roomDetailsDTO.name || defaultValues.name;
        this.label = roomDetailsDTO.label || defaultValues.label;
        this.operatorsMapper = OperatorItemsMapper.create(roomDetailsDTO.operators);
        mapItemsByKey(roomDetailsDTO.operators || defaultValues.operators, 'email', this.operators);
        this.formChecked = false;
        this.hostRoleConnectionId = roomDetailsDTO.hostRoleConnectionId || defaultValues.hostRoleConnectionId;
        const defaultConnecitonVersion = hasPermission(Permission.CREATE_NEW_ROOM_V2) ? ConnectionVersion.V2 : ConnectionVersion.V1;
        this.connectionVersion = this.isV2ConnectionVersion(roomDetailsDTO.connectionVersion || defaultConnecitonVersion);

        this.startDate = roomDetailsDTO.startDateTime
            ? utcToZonedTime(roomDetailsDTO.startDateTime, eventDTO.timeZone)
            : utcToZonedTime(eventDTO.startDateTime, eventDTO.timeZone);

        if (this.startDate) {
            this.startDate.setHours(0, 0, 0);
        }

        this.startDateTime = roomDetailsDTO.startDateTime
            ? utcToZonedTime(roomDetailsDTO.startDateTime, eventDTO.timeZone)
            : utcToZonedTime(eventDTO.startDateTime, eventDTO.timeZone);
        this.endDate = roomDetailsDTO.endDateTime
            ? utcToZonedTime(roomDetailsDTO.endDateTime, eventDTO.timeZone)
            : utcToZonedTime(eventDTO.startDateTime, eventDTO.timeZone);

        if (this.endDate) {
            this.endDate.setHours(0, 0, 0);
        }

        this.endDateTime = roomDetailsDTO.endDateTime
            ? utcToZonedTime(roomDetailsDTO.endDateTime, eventDTO.timeZone)
            : utcToZonedTime(eventDTO.startDateTime, eventDTO.timeZone);
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public getRoomLabelError(): string {
        return this.label.length > SHORT_PLAIN_TEXT_LENGTH
            ? this.intl.formatMessage(ROOM_LABEL_MAX_LENGTH_TEXT, {
                length: SHORT_PLAIN_TEXT_LENGTH
            })
            : null;
    }

    public getFieldValidationError(field: string): JSX.Element | undefined {
        return this.validationErrors[field];
    }

    public getConnectionVersion = (): boolean => {
        return this.connectionVersion;
    };

    @mobx.action
    public setConnectionVersion = (value: boolean): boolean => {
        return this.connectionVersion = value;
    };

    @mobx.action
    public checkForm(): boolean {
        this.formChecked = true;
        return !this.getDatesError() &&
            !this.getTimesError() && !this.getRoomLabelError();
    }

    public isFormChecked(): boolean {
        return this.formChecked;
    }

    @mobx.action
    public removeOperatorByEmail = (email: string): void => {
        delete this.operators[email];
    };

    @mobx.action
    public onOperatorRoleChange = (email: string, role: OperatorRole): void => {
        this.operators[email].operatorRole = role;
    };

    @mobx.action
    public setOperators(operators: IUserOperatorDTO[]): void {
        this.operators = {};
        operators.forEach(function(operator) {
            this[operator.email] = operator;
        }, this.operators);
    }

    public getOperators(): IUserOperatorDTO[] {
        return Object.values(this.operators);
    }

    public getOperatorsWithFullNameProperty(): IUserOperatorDTO[] {
        return Object.values(this.operators).map(operator => ({ ...operator, fullName: `${operator.firstName} ${operator.lastName}` }));
    }

    public getOperatorsWithoutFullNameProperty(): IOperatorsDTO[] {
        return Object.values(this.operators).reduce((_operators, operator) => {
            // eslint-disable-next-line no-unused-vars
            const { fullName, ...rest } = operator;
            return _operators.concat(rest);
        }, []);
    }

    public toApiDTO() {
        return {
            startDateTime: zonedTimeToUtc(this.startDateTime, this.eventDTO.timeZone),
            endDateTime: zonedTimeToUtc(this.endDateTime, this.eventDTO.timeZone),
            addedOperators: this.getAddedOperators(),
            removedOperators: this.getRemovedOperators(),
            updatedOperators: this.getUpdatedOperators(),
            hostRoleConnectionId: this.hostRoleConnectionId,
            label: this.label.trim(),
            connectionVersion: this.getParsedConnectionVersion(),
        };
    }

    public setHostRoleConnectionId(connectionId: number | null): void {
        this.hostRoleConnectionId = connectionId;
    }

    @mobx.action
    public setStartDate = (date: Date): void => {
        this.startDate = date;
        if (this.startDateTime) {
            this.startDateTime.setFullYear(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate());
        }
    };

    @mobx.action
    public setEndDate = (date: Date): void => {
        this.endDate = date;
        if (this.endDateTime) {
            this.endDateTime.setFullYear(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate());
        }
    };

    @mobx.action
    public setStartTime = (date: Date): void => {
        this.startTimeValidationError = false;
        this.startDateTime = new Date(this.startDate.getTime());
        this.startDateTime.setHours(date.getHours(), date.getMinutes());
    };

    @mobx.action
    public setEndTime = (date: Date): void => {
        this.endTimeValidationError = false;
        this.endDateTime = new Date(this.endDate.getTime());
        this.endDateTime.setHours(date.getHours(), date.getMinutes());
    };

    public getDatesError(): string {
        return this.startDate && this.endDate && !(isAfter(this.endDate, this.startDate) || isEqual(this.endDate, this.startDate))
            ? this.intl.formatMessage(SHOULD_NOT_BEFORE_START_TEXT)
            : null;
    }

    public getTimesError(): string {
        if (this.startTimeValidationError && this.endTimeValidationError) {
            return this.intl.formatMessage(INVALID_START_TIME_OR_END_TIME_TEXT);
        }
        if (this.startTimeValidationError) {
            return this.intl.formatMessage(INVALID_START_TIME_TEXT);
        }
        if (this.endTimeValidationError) {
            return this.intl.formatMessage(INVALID_END_TIME_TEXT);
        }
        if (this.startDateTime && this.endDateTime && !isBefore(this.startDateTime, this.endDateTime)) {
            return this.intl.formatMessage(CANNOT_BE_EQUAL_OR_GREATER_TEXT);
        }
        return null;
    }

    public getStartDateError(): string {
        return !this.startDate
            ? this.intl.formatMessage(FIELD_IS_REQUIRED_TEXT,
                {
                    field: 'Start date'
                }
            )
            : null;
    }

    public getEndDateError(): string {
        return !this.endDate
            ? this.intl.formatMessage(FIELD_IS_REQUIRED_TEXT,
                {
                    field: 'End date'
                }
            )
            : null;
    }

    public getStartTimeError() {
        return !this.startDateTime
            ? this.intl.formatMessage(FIELD_IS_REQUIRED_TEXT,
                {
                    field: 'Start time'
                }
            )
            : null;
    }

    public getEndTimeError() {
        return !this.endDateTime
            ? this.intl.formatMessage(FIELD_IS_REQUIRED_TEXT,
                {
                    field: 'End time'
                }
            )
            : null;
    }

    public getParsedConnectionVersion = (): ConnectionVersion => {
        return this.connectionVersion ? ConnectionVersion.V2 : ConnectionVersion.V1;
    };

    private getAddedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getAddedOperatorsToList(this.getOperatorsWithoutFullNameProperty());
    }

    private getRemovedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getRemovedOperatorsFromList(this.getOperatorsWithoutFullNameProperty());
    }

    private getUpdatedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getUpdatedOperatorsInList(this.getOperatorsWithoutFullNameProperty());
    }

    private isV2ConnectionVersion = (connectionVersion: ConnectionVersion): boolean => {
        return connectionVersion === ConnectionVersion.V2;
    };
}

export { RoomFormDataVM, defaultValues };
